/**
 * The Partner Portal's shell. The sidebar opens with the context switcher
 * (the partner, or the tenant you are in); inside /t/<tenant> the inverse
 * band names the tenant, its access and what you can do there.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { Shell, Crumbs, type NavSection, type SearchResult } from "../kit/Shell";
import { Icon } from "../kit/Icon";
import { api, post, useApi } from "../kit/api";
import { Dot, healthTone } from "../kit/ui";
import { Menu, Tip } from "../kit/overlays";
import { PORTFOLIO_CHANGED, useCan, useMe } from "./context";
import { ScopeStrip } from "./ScopeStrip";
import { MyNotifications } from "../pages/notifications/MyNotifications";
import type { Scopes } from "../../shared/access";
import { KEY_WORDS } from "../../shared/key-state";

export type TenantLite = { id: string; name: string; tier: string; status: string; health: string; scopes: Scopes; effective: Scopes; pinned: boolean; critical: number; warning: number };

const PAGES = [
  { to: "/", label: "Overview", section: "", icon: "grid", need: "" },
  { to: "/tenants", label: "Tenants", section: "", icon: "building-multiple", need: "tenants" },
  { to: "/alerts", label: "Alerts", section: "", icon: "alert", need: "tenants" },
  { to: "/baselines", label: "Baselines", section: "Manage", icon: "shield-task", need: "tenants" },
  { to: "/reports", label: "Reports", section: "Manage", icon: "document-data", need: "reports" },
  { to: "/billing", label: "Billing", section: "Manage", icon: "receipt-money", need: "business" },
  { to: "/team", label: "Team", section: "Partner", icon: "people-team", need: "" },
  { to: "/integrations", label: "Integrations", section: "Partner", icon: "plug-connected", need: "manage" },
  { to: "/branding", label: "Branding & sign-in", section: "Partner", icon: "paint-brush", need: "manage" },
  { to: "/notifications", label: "Notifications", section: "Partner", icon: "alert", need: "" },
  { to: "/support", label: "Inlinea support", section: "Partner", icon: "chat-help", need: "" },
  { to: "/audit", label: "Audit trail", section: "Partner", icon: "history", need: "" },
];

const TENANT_PAGES = [
  { to: "", label: "Overview", icon: "grid" },
  { to: "control-center", label: "Control Center", icon: "window-shield" },
  { to: "access", label: "Access key", icon: "key" },
  { to: "features", label: "Features", icon: "shield-task" },
  { to: "contract", label: "Contract & reports", icon: "receipt" },
];

/** The tenant band's two marks: whether the key works (shared/key-state.ts), and how the tenant itself is. */
const HEALTH_WORDS: Record<string, [string, string]> = {
  healthy: ["Healthy", "Every platform service operational at the last check."],
  warning: ["Degraded", "A platform service was degraded at the last check."],
  critical: ["Critical", "A platform service was down, or the tenant didn't answer, at the last check."],
  unknown: ["Not checked", "No reading yet."],
};

export function Layout({ children }: { children: ReactNode }) {
  const me = useMe();
  const can = useCan();
  const loc = useLocation();
  const nav = useNavigate();
  const [myNotifications, setMyNotifications] = useState(false);
  const overview = useApi<{ tenants: TenantLite[]; counts: { alerts: number; tenants: number } }>(can.tenants ? "/api/overview" : null, { poll: 60_000 });
  const announcements = useApi<{ id: string; title: string; message: string; tone: string }[]>("/api/announcements", { poll: 300_000 });
  const m = /^\/t\/([^/]+)(?:\/([^/]+))?/.exec(loc.pathname);
  const tenantId = m?.[1] ?? null;
  const tenants = overview.data?.tenants ?? [];
  const t = tenants.find((x) => x.id === tenantId);
  // Connecting or disconnecting a tenant changes the switcher, the counts and the band at once.
  const reloadOverview = useRef(overview.reload);
  reloadOverview.current = overview.reload;
  useEffect(() => {
    const again = () => reloadOverview.current();
    window.addEventListener(PORTFOLIO_CHANGED, again);
    return () => window.removeEventListener(PORTFOLIO_CHANGED, again);
  }, []);

  const sections: NavSection[] = useMemo(() => {
    if (tenantId) return [{ items: TENANT_PAGES.map((p) => ({ to: `/t/${tenantId}${p.to ? `/${p.to}` : ""}`, label: p.label, icon: p.icon, end: !p.to })) }];
    const groups: NavSection[] = [];
    for (const p of PAGES) {
      if (p.need === "tenants" && !can.tenants) continue;
      if (p.need === "business" && !can.business) continue;
      if (p.need === "manage" && !can.manage) continue;
      if (p.need === "reports" && !can.reports) continue;
      let g = groups.find((x) => (x.title ?? "") === p.section);
      if (!g) groups.push((g = { title: p.section || undefined, items: [] }));
      g.items.push({ to: p.to, label: p.label, icon: p.icon, end: p.to === "/", count: p.to === "/tenants" ? overview.data?.counts.tenants : p.to === "/alerts" ? overview.data?.counts.alerts : undefined, badge: p.to === "/alerts" });
    }
    return groups;
  }, [tenantId, can, overview.data]);

  const switcher = (
    <Menu
      align="start"
      block
      items={[
        { label: "Portfolio", icon: "building-multiple", hint: tenantId ? undefined : "Current", onClick: () => nav("/") },
        { label: "All tenants", icon: "list", hint: String(tenants.length), onClick: () => nav("/tenants") },
        "sep",
        ...[...tenants].sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.critical - a.critical || a.name.localeCompare(b.name)).slice(0, 25).map((x) => ({ label: x.name, icon: x.pinned ? "pin" : "building", hint: x.id === tenantId ? "Current" : x.critical ? `${x.critical} critical` : undefined, onClick: () => nav(`/t/${x.id}`) })),
      ]}
      trigger={(open, isOpen) => (
        <button className="switcher" onClick={open} aria-label="Switch partner or tenant" aria-haspopup="menu" aria-expanded={isOpen}>
          <span className="avatar" style={{ width: 30, height: 30, background: tenantId ? "var(--blF)" : "var(--inv)" }}>{(t?.name ?? me.partner.name).slice(0, 2).toUpperCase()}</span>
          <span className="grow" style={{ minWidth: 0 }}>
            <div className="ellipsis" style={{ fontWeight: 600 }}>{t?.name ?? me.partner.name}</div>
            <div className="faint ellipsis" style={{ fontSize: 11.5 }}>{t ? `${t.tier[0].toUpperCase()}${t.tier.slice(1)} · tenant` : `Portfolio · ${tenants.length} ${tenants.length === 1 ? "tenant" : "tenants"}`}</div>
          </span>
          <Icon name="chevron-up-down" size={14} style={{ color: "var(--tx3)" }} />
        </button>
      )}
    />
  );

  const crumbs = tenantId ? (
    <Crumbs items={[{ label: me.partner.name, to: "/" }, { label: "Tenants", to: "/tenants" }, t?.name ?? "Tenant", TENANT_PAGES.find((p) => p.to === (m?.[2] ?? ""))?.label ?? "Overview"]} />
  ) : (
    <Crumbs items={[me.partner.name, PAGES.filter((p) => (p.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(p.to))).pop()?.label ?? "Overview"]} />
  );

  const band = tenantId ? (
    <>
      <button onClick={() => nav("/tenants")}><Icon name="arrow-left" size={14} /> Portfolio</button>
      <span className="bsep" />
      {t ? <b>{t.name}</b> : <span className="skel" style={{ width: 120, height: 12, opacity: 0.4 }} />}
      {t && (
        <span className="row" style={{ gap: 10, border: "1px solid var(--invSep)", borderRadius: 6, padding: "2px 8px" }}>
          <Tip text={KEY_WORDS[t.status]?.[1] ?? "The key's state at the last check."}>
            <span className="row" style={{ gap: 6 }} tabIndex={0}><Dot tone={t.status === "connected" ? "ok" : t.status === "expiring" ? "am" : "rd"} />{KEY_WORDS[t.status]?.[0] ?? t.status}</span>
          </Tip>
          <span className="bsep" />
          <Tip text={HEALTH_WORDS[t.health]?.[1] ?? "What the tenant reported at the last check."}>
            <span className="row" style={{ gap: 6 }} tabIndex={0}><Dot tone={healthTone(t.health)} />{HEALTH_WORDS[t.health]?.[0] ?? t.health}</span>
          </Tip>
        </span>
      )}
      {t && <span className="row" style={{ gap: 6 }}>Your access <ScopeStrip scopes={t.effective} /></span>}
      <span className="right row" style={{ gap: 10, color: "var(--invTx2)" }}><Icon name="eye" size={14} /> Audited in both trails</span>
    </>
  ) : undefined;

  const quick: SearchResult[] = useMemo(
    () => [
      ...PAGES.map((p) => ({ kind: "Page", title: p.label, sub: p.section || "Partner Portal", href: p.to, icon: p.icon })),
      ...tenants.map((x) => ({ kind: "Tenant", title: x.name, sub: x.tier, href: `/t/${x.id}`, icon: "building" })),
    ],
    [tenants],
  );

  return (
    <Shell
      product={{ name: me.partner.branding.name || "Inlinea", sub: "Partner Portal" }}
      nav={sections}
      header={switcher}
      crumbs={crumbs}
      band={band}
      searchLabel="Find a tenant"
      searchPlaceholder="Search customer tenants and pages"
      topRight={
        <>
          {(announcements.data ?? []).slice(0, 1).map((a) => <span key={a.id} className="envchip" title={a.message}><Icon name="megaphone" size={14} />{a.title}</span>)}
          {me.environment !== "production" && <span className="envchip"><span className="live am" />{me.environment[0].toUpperCase() + me.environment.slice(1)}</span>}
        </>
      }
      user={{
        name: me.member.name,
        email: me.member.email,
        lines: <div className="faint ellipsis" style={{ fontSize: 12, marginTop: 2 }}>{me.role.label} · MSP ID <span className="mono">{me.partner.msp_id}</span></div>,
        menu: [
          { label: "My notifications", icon: "alert", onClick: () => setMyNotifications(true) },
          { label: "Team and roles", icon: "people-team", onClick: () => nav("/team") },
          // The product docs' partner area; the docs ask the partner to sign in (Inlinea-brain/docs/docs-access.md).
          { label: "Documentation", icon: "book-open", onClick: () => window.open("https://inlineasecurity.com/docs/partners/", "_blank", "noopener") },
          { label: "Sign out", icon: "sign-out", onClick: () => post("/auth/logout").finally(() => window.location.assign("/signin")) },
        ],
      }}
      version={
        me.version.version ? (
          <>
            <b className="mono">v{me.version.version}</b>
            <span className="faint mono">Build {me.version.build}</span>
          </>
        ) : (
          <span className="faint">{me.version.unavailable ? "Release unavailable" : "No release recorded yet"}</span>
        )
      }
      search={(q) => api<{ results: SearchResult[] }>(`/api/search?q=${encodeURIComponent(q)}`).then((r) => r.results.map((x) => ({ ...x, icon: "building" })))}
      quick={quick}
    >
      {children}
      {myNotifications && <MyNotifications onClose={() => setMyNotifications(false)} />}
    </Shell>
  );
}
